import React from 'react';
import { motion } from 'framer-motion';
import { 
  Award,
  Shield, 
  Brain,
  Lock,
  Target,
  Zap,
  CheckCircle,
  ArrowRight,
  Globe,
  Server,
  Cloud,
  Database,
  Network,
  Users
} from 'lucide-react';

interface SecurityExcellenceProps {
  setCurrentPage: (page: string) => void;
}

const SecurityExcellence: React.FC<SecurityExcellenceProps> = ({ setCurrentPage }) => {
  const expertise = [
    {
      icon: Brain,
      title: "Advanced AI & Machine Learning",
      description: "Pioneering next-generation security with artificial intelligence",
      capabilities: [
        "Predictive threat detection",
        "Behavioral analytics",
        "Automated response systems",
        "Pattern recognition"
      ]
    },
    {
      icon: Shield,
      title: "Zero-Trust Architecture",
      description: "Implementing comprehensive security frameworks",
      capabilities: [
        "Identity-based access",
        "Micro-segmentation",
        "Continuous verification",
        "Least privilege access"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Security Innovation",
      description: "Securing modern cloud infrastructures",
      capabilities: [
        "Multi-cloud protection",
        "Cloud-native security",
        "Workload protection",
        "Container security"
      ]
    },
    {
      icon: Network,
      title: "Network Defense",
      description: "Advanced network security and monitoring",
      capabilities: [
        "Deep packet inspection",
        "Traffic analysis",
        "Threat hunting",
        "Network segmentation"
      ]
    }
  ];

  const innovations = [
    {
      icon: Lock,
      title: "Quantum-Ready Security",
      description: "Future-proofing security for the quantum era",
      features: [
        "Post-quantum cryptography",
        "Quantum-safe algorithms",
        "Key distribution",
        "Encryption standards"
      ]
    },
    {
      icon: Database,
      title: "Advanced Data Protection",
      description: "Next-generation data security solutions",
      features: [
        "Homomorphic encryption",
        "Secure data sharing",
        "Privacy computing",
        "Data tokenization"
      ]
    },
    {
      icon: Server,
      title: "Autonomous Security",
      description: "Self-healing security infrastructure",
      features: [
        "Auto-remediation",
        "Dynamic defense",
        "Adaptive policies",
        "Continuous optimization"
      ]
    }
  ];

  const researchAreas = [
    {
      icon: Target,
      title: "Threat Research",
      description: "Leading-edge threat intelligence and research",
      areas: [
        "APT detection",
        "Malware analysis",
        "Zero-day research",
        "Threat modeling"
      ]
    },
    {
      icon: Globe,
      title: "Security Innovation",
      description: "Pioneering new security technologies",
      areas: [
        "Blockchain security",
        "IoT protection",
        "5G/6G security",
        "Edge computing"
      ]
    },
    {
      icon: Users,
      title: "Human-Centric Security",
      description: "Advanced user behavior analytics",
      areas: [
        "Identity protection",
        "Behavior modeling",
        "Access patterns",
        "Risk scoring"
      ]
    }
  ];

  const metrics = [
    {
      icon: Zap,
      title: "Response Time",
      value: "< 15 mins",
      description: "Average incident response time"
    },
    {
      icon: Shield,
      title: "Threat Detection",
      value: "99.99%",
      description: "Threat detection accuracy"
    },
    {
      icon: Lock,
      title: "Security Coverage",
      value: "100%",
      description: "Infrastructure protection"
    },
    {
      icon: Brain,
      title: "AI Analysis",
      value: "24/7",
      description: "Continuous monitoring"
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Award className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Security Excellence & Innovation</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Pioneering the future of cybersecurity with advanced technology and unmatched expertise
          </motion.p>
        </div>

        {/* Core Expertise */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Core Expertise</h2>
            <p className="text-lg text-muted">
              Industry-leading capabilities in advanced cybersecurity
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {expertise.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <item.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="ml-4">
                    <h3 className="text-2xl font-semibold">{item.title}</h3>
                    <p className="text-muted">{item.description}</p>
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {item.capabilities.map((capability, idx) => (
                    <li key={idx} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{capability}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Innovation Showcase */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Security Innovations</h2>
            <p className="text-lg text-muted">
              Pioneering next-generation security technologies
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {innovations.map((innovation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <innovation.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold ml-4">{innovation.title}</h3>
                </div>
                <p className="text-muted mb-6">{innovation.description}</p>
                <ul className="space-y-3">
                  {innovation.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Research & Development */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Research & Development</h2>
            <p className="text-lg text-muted">
              Advancing cybersecurity through continuous innovation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {researchAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <area.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold ml-4">{area.title}</h3>
                </div>
                <p className="text-muted mb-6">{area.description}</p>
                <ul className="space-y-3">
                  {area.areas.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Performance Metrics */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Performance Excellence</h2>
            <p className="text-lg text-muted">
              Industry-leading security metrics and capabilities
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {metrics.map((metric, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 text-center hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg mx-auto mb-4">
                  <metric.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{metric.title}</h3>
                <div className="text-3xl font-bold text-gradient mb-2">{metric.value}</div>
                <p className="text-muted">{metric.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Experience Security Excellence</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Partner with us to leverage cutting-edge security technology and unmatched expertise
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Start Your Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SecurityExcellence;